import moment from "moment-timezone";

/**
 * Formats a Date object into a string with the format "DD-MM-YYYY HH:mm:ss".
 * Uses the "en-GB" locale for consistent day/month/year ordering.
 *
 * @param {Date} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const dateFormatter = (date) => {
  return date
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    .replace(/(\d+)\/(\d+)\/(\d+), (\d+:\d+:\d+)/, "$1-$2-$3 $4");
};

/**
 * Converts a date string in "DD-MM-YYYY HH:mm:ss" format to ISO 8601 format.
 *
 * @param {string} date - The date string to convert.
 * @returns {string} The ISO 8601 formatted date string, or empty string if input is invalid.
 */
export const dateFormatterISO = (date) => {
  if (!date) return "";
  const [datePart, timePart] = date.split(" ");
  const [day, month, year] = datePart.split("-");
  const [hours, minutes, seconds] = timePart.split(":");
  const isoDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  return isoDateString;
};

/**
 * Formats a date string into a short table-friendly format.
 * Example: "25 Dec 2023, 10:30 AM"
 *
 * @param {string} dateString - The date string to format.
 * @param {boolean} [time=true] - Whether to include the time in the output.
 * @returns {string} The formatted date string, or "-" if input is invalid.
 */
export const tableShortFormatDate = (dateString, time = true) => {
  if (!dateString || dateString == "") return "-";
  const parsed = moment(dateString);
  if (!parsed.isValid()) return "-";
  return parsed.format(`DD MMM YYYY${time ? ", HH:mm A" : ""}`);
};

/**
 * Formats a date for Address Book cards.
 * Example: "18 May, 2026 05:54:26"
 */
export const addressBookFormatDate = (dateString) => {
  if (!dateString) return "-";
  const parsed = moment(dateString);
  if (!parsed.isValid()) return "-";
  return parsed.format("DD MMM, YYYY HH:mm:ss");
};

/**
 * Formats a message date string in "DD-MM-YYYY HH:mm:ss" format to a short readable format.
 * Example: "25 Dec 2023, 10:30 AM"
 *
 * @param {string} dateString - The date string to format.
 * @param {boolean} [time=true] - Whether to include the time in the output.
 * @returns {string} The formatted date string, or "-" if input is invalid.
 */
export const msgShortFormatDate = (dateString, time = true) => {
  if (!dateString) return "-";
  const parsed = moment(dateString, "DD-MM-YYYY HH:mm:ss", true);
  if (!parsed.isValid()) return "-";
  return parsed.format(`DD MMM YYYY${time ? ", hh:mm A" : ""}`);
};

/**
 * Converts a date string from "DD-MM-YYYY HH:mm:ss" to "YYYY-MM-DD HH:mm:ss" format.
 *
 * @param {string|Date} dateString - The date string to convert or Date object.
 * @returns {string} The reformatted date string.
 */
export const smsFormatDate = (dateString) => {
  if (!dateString) return "";

  if (dateString instanceof Date) {
    dateString = dateFormatter(dateString);
  }

  if (typeof dateString !== "string") return "";

  return dateString.replace(/(\d+)-(\d+)-(\d+) (\d+:\d+:\d+)/, "$3-$2-$1 $4");
};

/**
 * Formats a date string into a long, human-readable format for tables.
 * Example: "December 25, 2023, 10:30:00 AM"
 *
 * @param {string} dateString - The date string to format.
 * @returns {string} The formatted date string.
 */
export const tableFormatDate = (dateString) => {
  const date = new Date(dateString);
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
};

/**
 * Formats a Date object into a string with optional time and order.
 * If reversed is true, returns "MM-DD-YYYY", otherwise "YYYY-MM-DD HH:mm:ss".
 *
 * @param {Date} date - The date to format.
 * @param {boolean} [endDate=true] - If true, sets time to "23:59:59", else "00:00:00".
 * @param {boolean} [reversed=false] - If true, returns "MM-DD-YYYY" format.
 * @returns {string} The formatted date string.
 */
export const dateFormatter2 = (date, endDate = true, reversed = false) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  if (reversed) return `${month}-${day}-${year}`;
  return `${year}-${month}-${day} ` + (endDate ? "23:59:59" : "00:00:00");
};

/**
 * Returns the date range for the last 10 days, formatted as strings.
 *
 * @param {boolean} [reversed=false] - If true, returns dates in "MM-DD-YYYY" format.
 * @returns {{startDate: string, endDate: string}} The start and end date strings.
 */
export const getLast10thDate = (reversed = false) => {
  let currentDate = new Date();
  let endDate = dateFormatter2(currentDate, true, reversed);
  let startDate = new Date();
  startDate.setDate(startDate.getDate() - 10 + 1);
  startDate = dateFormatter2(startDate, false, reversed);
  return { startDate, endDate };
};

/**
 * Returns the first date of the current month, formatted as a string.
 *
 * @param {boolean} [reversed=false] - If true, returns date in "MM-DD-YYYY" format.
 * @returns {string} The formatted first date of the month.
 */
export const getFirstDateOfMonth = (reversed = false) => {
  let currentDate = new Date();
  let endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  endDate = dateFormatter2(endDate, false, reversed);
  return endDate;
};

/**
 * Counts the occurrences of each unique value for a given key in an array of objects.
 *
 * @param {Array<Object>} data - The data array to process.
 * @param {string} keyName - The key to count by.
 * @returns {{labels: string[], range: number[]}} An object with unique labels and their counts.
 */
export const getCountsByKey = (data, keyName) => {
    if (!Array.isArray(data)) return { labels: [], range: [] }; 
  const counts = data.reduce((counts, row) => {
    counts[row[keyName]] = (counts[row[keyName]] || 0) + 1;
    return counts;
  }, {});
 return { labels: Object.keys(counts), range: Object.values(counts) };
};
